import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { ScheduleModal } from "../components/ScheduleModal";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { SkeletonList } from "../components/ui/Skeleton";
import { useReschedule } from "../hooks/useReschedule";
import { usePublishNow } from "../hooks/usePublishNow";
import { useUnschedule } from "../hooks/useUnschedule";
import { useDeletePost } from "../hooks/useDeletePost";
import { toast } from "../lib/toast";
import type { Draft } from "../lib/api";

const Scheduled = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["scheduled"],
    queryFn: () => api.posts.scheduled(),
  });

  const reschedule = useReschedule();
  const publishNow = usePublishNow();
  const unschedule = useUnschedule();
  const deletePost = useDeletePost();

  const [reschedulePost, setReschedulePost] = useState<Draft | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);

  const handleReschedule = (scheduledAt: string | undefined) => {
    if (!reschedulePost || !scheduledAt) return;
    reschedule.mutate(
      { id: reschedulePost.id, scheduledAt },
      {
        onSuccess: () => toast.success("Post reagendado"),
        onError: (err: Error) =>
          toast.error("Falha ao reagendar", { description: err.message }),
      }
    );
    setReschedulePost(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Agendados</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          Posts aprovados, aguardando publicação.
        </p>
      </div>
      {isLoading && <SkeletonList count={4} />}
      {!isLoading && (!data || data.length === 0) && (
        <EmptyState
          title="Nada agendado"
          message="Aprove um draft em /drafts pra ver ele aqui."
        />
      )}
      {data && data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map((d) => (
            <ScheduledCard
              key={d.id}
              draft={d}
              onReschedule={() => setReschedulePost(d)}
              onPublishNow={() =>
                publishNow.mutate(
                  { id: d.id },
                  {
                    onSuccess: () => toast.success("Publicação iniciada"),
                    onError: (err: Error) =>
                      toast.error("Falha ao publicar agora", { description: err.message }),
                  }
                )
              }
              onUnschedule={() =>
                unschedule.mutate(
                  { id: d.id },
                  {
                    onSuccess: () => toast.success("Voltou para rascunho"),
                    onError: (err: Error) =>
                      toast.error("Falha ao desagendar", { description: err.message }),
                  }
                )
              }
              onDelete={() => setDeleteCandidate(d.id)}
            />
          ))}
        </div>
      )}

      <ScheduleModal
        isOpen={reschedulePost !== null}
        onClose={() => setReschedulePost(null)}
        onSubmit={handleReschedule}
        mode="reschedule"
      />

      <ConfirmDialog
        open={deleteCandidate !== null}
        title="Deletar este post agendado?"
        description="O post sairá da fila e não será publicado. Esta ação não pode ser desfeita."
        confirmLabel="Deletar"
        cancelLabel="Cancelar"
        destructive
        loading={deletePost.isPending}
        onConfirm={() => {
          if (deleteCandidate) {
            deletePost.mutate(
              { id: deleteCandidate, kind: "draft" },
              {
                onSuccess: () => toast.success("Post agendado deletado"),
                onError: (err: Error) =>
                  toast.error("Falha ao deletar", { description: err.message }),
                onSettled: () => setDeleteCandidate(null),
              }
            );
          }
        }}
        onCancel={() => setDeleteCandidate(null)}
      />
    </div>
  );
};

export default Scheduled;

interface CardProps {
  draft: Draft;
  onReschedule: () => void;
  onPublishNow: () => void;
  onUnschedule: () => void;
  onDelete: () => void;
}

const ScheduledCard = ({ draft, onReschedule, onPublishNow, onUnschedule, onDelete }: CardProps) => {
  const scheduledFmt = draft.scheduledAt
    ? new Date(draft.scheduledAt).toLocaleString("pt-BR", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "?";

  return (
    <div
      data-testid={`post-${draft.id}`}
      className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
          Agendado: {scheduledFmt}
        </span>
        <div className="flex items-center gap-2">
          {draft.status === "paused" && (
            <Badge variant="warning" size="sm">
              Pausado — token expirou
            </Badge>
          )}
          <span className="text-xs text-zinc-400 dark:text-zinc-500">{draft.format}</span>
        </div>
      </div>
      <p className="text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap break-words leading-relaxed line-clamp-4">
        {draft.content.slice(0, 220)}
        {draft.content.length > 220 && (
          <span className="text-zinc-400 dark:text-zinc-500">...</span>
        )}
      </p>
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <Button variant="primary" size="sm" onClick={onPublishNow}>
          Publicar agora
        </Button>
        <Link
          to={`/drafts/${draft.id}`}
          className="inline-flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 h-7 text-xs font-medium text-zinc-900 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors focus-ring"
        >
          Editar
        </Link>
        <Button variant="secondary" size="sm" onClick={onReschedule}>
          Remarcar
        </Button>
        <Button variant="ghost" size="sm" onClick={onUnschedule}>
          Voltar pra rascunho
        </Button>
        <div className="ml-auto">
          <button
            type="button"
            onClick={onDelete}
            className="text-xs text-red-600 dark:text-red-400 hover:underline px-2 h-7 inline-flex items-center"
          >
            Deletar
          </button>
        </div>
      </div>
    </div>
  );
};
